import { createAuthApi, type AuthUser } from '@e-learning/core';
import { i18n, Login } from '@e-learning/shared';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../api/client';
import { authStorage } from '../../../auth';
import { getDefaultLoginBranding } from './loginBranding';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/** Códigos de erro do core (auth) — quando ocorrem, exibir t('login.error_generic') */
const LOGIN_ERROR_GENERIC = 'LOGIN_ERROR_GENERIC';
const LOGIN_INVALID_RESPONSE = 'LOGIN_INVALID_RESPONSE';

const authApi = createAuthApi(api);

export interface LoginPageProps {
  /** Chamado após login com sucesso com o usuário retornado pela API (token já salvo no localStorage) */
  onLogin: (user: AuthUser) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const branding = getDefaultLoginBranding(t);

  const handleLoginSubmit = async (credentials: {
    email: string;
    password: string;
  }) => {
    try {
      const response = await authApi.login(credentials, {
        acceptLanguage: i18n.language || 'en-US',
      });

      const user = response?.value;
      if (!user?.token) {
        throw new Error(t('login.error_generic'));
      }

      // Salva dados completos no localStorage (authStorage)
      authStorage.saveAuthData(user);

      // Sincroniza token e refreshToken para o API client (access_token / refresh_token)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(ACCESS_TOKEN_KEY, user.token);
        window.localStorage.setItem(REFRESH_TOKEN_KEY, user.refreshToken ?? '');
      }

      onLogin(user);
    } catch (error) {
      if (error instanceof Error) {
        const msg = error.message;
        if (msg === LOGIN_ERROR_GENERIC || msg === LOGIN_INVALID_RESPONSE) {
          throw new Error(t('login.error_generic'));
        }
        throw error;
      }
      throw new Error(t('login.error_generic'));
    }
  };

  return (
    <div data-e2e="page-admin-login">
      <Login
        branding={branding}
        onLogin={handleLoginSubmit}
        onForgotPassword={(email: string) => {
          const trimmed = email?.trim() ?? '';
          if (!trimmed) {
            navigate('/forgot-password');
            return;
          }
          navigate(`/forgot-password?email=${encodeURIComponent(trimmed)}`);
        }}
      />
    </div>
  );
}
